import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  currency: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  initialized: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ needsEmailConfirmation: boolean }>;
  signOut: () => Promise<void>;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Clear auth state
  const clearAuthState = () => {
    setUser(null);
    setProfile(null);
    setLoading(false);
    localStorage.removeItem('supabase.auth.token');
    sessionStorage.removeItem('supabase.auth.token');
  };

  const fetchProfile = async (userId: string, fullNameFromMetadata?: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          const newProfile = await createProfile(userId, fullNameFromMetadata || '');
          setProfile(newProfile);
        } else {
          console.error('Error fetching profile:', error);
          setProfile(null);
        }
      } else {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error in fetchProfile:', error);
      setProfile(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          if (mounted) clearAuthState();
          return;
        }

        if (mounted && session?.user) {
          setUser(session.user);
          const fullName = (session.user.user_metadata?.full_name as string) || '';
          fetchProfile(session.user.id, fullName).catch((err) =>
            console.error('Background profile fetch failed:', err)
          );
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) clearAuthState();
      } finally {
        if (mounted) {
          setInitialized(true);
          setLoading(false);
        }
      }
    };

    // Set up auth state change listener first (can fire before getSession resolves)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;

      if (session?.user) {
        setUser(session.user);
        setInitialized(true);
        setLoading(false);
        const fullName = (session.user.user_metadata?.full_name as string) || '';
        fetchProfile(session.user.id, fullName).catch((err) =>
          console.error('Profile fetch failed:', err)
        );
      } else {
        clearAuthState();
        setInitialized(true);
        setLoading(false);
      }
    });

    initialize();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  async function createProfile(userId: string, fullName: string) {
    try {
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (existingProfile) {
        return existingProfile;
      }

      const { data: newProfile, error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          full_name: fullName,
          currency: 'USD'
        })
        .select()
        .single();

      if (insertError) throw insertError;
      return newProfile;
    } catch (error) {
      console.error('Error in createProfile:', error);
      throw error;
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      // Clear any existing session first
      await supabase.auth.signOut();
      clearAuthState();
      
      const { error, data } = await supabase.auth.signInWithPassword({ 
        email, 
        password
      });
      
      if (error) throw error;

      if (data.user) {
        setUser(data.user);
        await fetchProfile(data.user.id);
      }
    } catch (error) {
      console.error('Sign in error:', error);
      clearAuthState();
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, fullName: string): Promise<{ needsEmailConfirmation: boolean }> => {
    try {
      setLoading(true);
      
      // Clear any existing session first
      await supabase.auth.signOut();
      clearAuthState();
      
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
        }
      });

      if (error) throw error;
      
      if (data.user) {
        // Only create profile when we have a session (user is immediately signed in).
        // When email confirmation is required, session is null until they confirm.
        if (data.session) {
          await createProfile(data.user.id, fullName);
          setUser(data.user);
          return { needsEmailConfirmation: false };
        } else {
          // Email confirmation required - don't set user (no session). Profile created on confirm.
          return { needsEmailConfirmation: true };
        }
      }
      return { needsEmailConfirmation: false };
    } catch (error) {
      console.error('Sign up error:', error);
      clearAuthState();
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      clearAuthState();
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: Partial<Profile>) => {
    if (!user) throw new Error('No user');

    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...data,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      await fetchProfile(user.id);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const value = {
    user,
    profile,
    loading,
    initialized,
    signIn,
    signUp,
    signOut,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}